import { Delete, Edit, PersonAdd, PictureAsPdf, TableView } from "@mui/icons-material";
import { Box, Button, Chip, TextField, Tooltip } from "@mui/material";
import Paper from "@mui/material/Paper";
import {
    type GridColDef,
    GridPagination,
    type GridRowSelectionModel
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import type { Usuario } from "../../firebase/firestore/usuarios";
import { validateWebUserLimit } from "../../utils/agencyValidations";
import { exportExcelUsers, exportPDFUsers } from "../../utils/exportTable";
import { useAgenciaStore } from "../../zustand/useAgenciaStore";
import { useDateRangeStore } from "../../zustand/useDateRangeStore";
import { useUsuariosStore } from "../../zustand/useUsuariosStore";
import ModalCreateUser from "../modals/ModalCreateUser";
import ModalDeleteUser from "../modals/ModalDeleteUser";
import ModalFormUser from "../modals/ModalFormUser";
import ModalUserLimit from "../modals/ModalUserLimit";
import DataTable from "./DataTable";

export default function TableUsers() {
    const { agencia } = useAgenciaStore();
    const [isOpen, setIsOpen] = useState(false);
    const [isOpenDelete, setIsOpenDelete] = useState(false);
    const [isOpenCreate, setIsOpenCreate] = useState(false);
    const [isOpenUserLimit, setIsOpenUserLimit] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 20,
    });
    const [search, setSearch] = useState("");
    const [entity, setEntity] = useState<Usuario>();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setSelectionModel] = useState<GridRowSelectionModel>();

    const { countTotal, allUsuarios, resetUsuarios, fetchNextPage } = useUsuariosStore();
    const { fechaInicio, fechaFin } = useDateRangeStore();

    // biome-ignore lint/correctness/useExhaustiveDependencies: recargar tabla al cambiar rango de fechas
    useEffect(() => {
        resetUsuarios();
        fetchNextPage();
    }, [resetUsuarios, fetchNextPage, fechaInicio, fechaFin]);

    const handleIsOpenModal = (b?: boolean) => setIsOpen(b ?? !isOpen);

    const filteredRows = allUsuarios.filter((row) => {
        if (!search.trim()) return true;
        
        const searchLower = search.toLowerCase();
        
        // Search in id
        if (row.id?.toLowerCase().includes(searchLower)) return true;
        
        // Search in lockerCode
        if (row.lockerCode?.toLowerCase().includes(searchLower)) return true;
        
        // Search in name
        if (row.name?.toLowerCase().includes(searchLower)) return true;
        
        // Search in lastName
        if (row.lastName?.toLowerCase().includes(searchLower)) return true;
        
        // Search in email
        if (row.email?.toLowerCase().includes(searchLower)) return true;
        
        // Search in phone (countryCode + phone)
        if (row.countryCode && row.phone) {
            const phoneStr = `${row.countryCode} ${row.phone}`.toLowerCase();
            if (phoneStr.includes(searchLower)) return true;
        }
        
        // Search in aereo
        if (row.aereo !== undefined && row.aereo !== null) {
            if (String(row.aereo).includes(searchLower)) return true;
        }
        
        // Search in maritimo
        if (row.maritimo !== undefined && row.maritimo !== null) {
            if (String(row.maritimo).includes(searchLower)) return true;
        }
        
        // Search in total
        if (row.total !== undefined && row.total !== null) {
            if (String(row.total).includes(searchLower)) return true;
        }
        
        // Search in puntos
        if (row.puntos !== undefined && row.puntos !== null) {
            if (String(row.puntos).includes(searchLower)) return true;
        }
        
        // Search in tarifaAerea
        if (row.tarifaAerea) {
            if (row.tarifaAerea.nombre?.toLowerCase().includes(searchLower)) return true;
            if (row.tarifaAerea.monto !== undefined && String(row.tarifaAerea.monto).includes(searchLower)) return true;
            if (row.tarifaAerea.moneda !== undefined && String(row.tarifaAerea.moneda).toLowerCase().includes(searchLower)) return true;
        }
        
        // Search in tarifaMaritima
        if (row.tarifaMaritima) {
            if (row.tarifaMaritima.nombre?.toLowerCase().includes(searchLower)) return true;
            if (row.tarifaMaritima.monto !== undefined && String(row.tarifaMaritima.monto).includes(searchLower)) return true;
            if (row.tarifaMaritima.moneda !== undefined && String(row.tarifaMaritima.moneda).toLowerCase().includes(searchLower)) return true;
        }
        
        // Search in role
        if (row.role) {
            if (row.role.toLowerCase().includes(searchLower)) return true;
        }

        // Search in estado
        if (row.estado && row.estado.toLowerCase().includes(searchLower)) return true;

        // Search in vendedor / referidoPor
        if (row.vendedor?.name?.toLowerCase().includes(searchLower)) return true;
        if (row.referidoPor?.name?.toLowerCase().includes(searchLower)) return true;

        return false;
    });

    const handleEdit = (usuario: Usuario) => {
        setEntity(usuario);
        setIsOpen(!isOpen);
    };

    const handleDelete = (usuario: Usuario) => {
        setEntity(usuario);
        setIsOpenDelete(true);
    };

    const handleCloseModal = () => {
        handleIsOpenModal(false);
        setEntity(undefined);
    };

    const handleCloseDeleteModal = () => {
        setIsOpenDelete(false);
        setEntity(undefined);
    };

    const handleOpenCreateModal = async () => {
        // agencia.webUserLimit limita cuantos usuarios se crean desde la web
        if (await validateWebUserLimit(agencia)) {
            setIsOpenCreate(true);
        } else {
            setIsOpenUserLimit(true);
        }
    };

    const handleCloseCreateModal = () => {
        setIsOpenCreate(false);
    };

    const columns: GridColDef[] = [
        {
            field: "acciones",
            headerName: "Acciones",
            width: 150,
            renderCell: (params) => (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", gap: 1 }}>
                    <Button
                        variant="outlined"
                        sx={{ minWidth: "auto", padding: "5px" }}
                        onClick={() => handleEdit(params.row)}
                        title="Editar usuario"
                    >
                        <Edit sx={{ fontSize: 20 }} />
                    </Button>
                    <Button
                        variant="outlined"
                        color="error"
                        sx={{ minWidth: "auto", padding: "5px" }}
                        onClick={() => handleDelete(params.row)}
                        title="Eliminar usuario"
                    >
                        <Delete sx={{ fontSize: 20 }} />
                    </Button>
                </Box>
            ),
        },
        { field: "lockerCode", headerName: "Cliente", width: 120, sortable: true },
        {
            field: "role",
            headerName: "Rol",
            width: 120,
            sortable: true,
            valueGetter: (_value, row) => row.role || "",
            renderCell: (params) => {
                const role = params.row.role;
                return role || "-";
            }
        },
        {
            field: "estado",
            headerName: "Estado",
            width: 130,
            sortable: true,
            valueGetter: (_value, row) => row.estado || "",
            renderCell: (params) => {
                const estado = params.row.estado as string | undefined;
                if (!estado) return "-";
                return (
                    <Chip
                        label={estado}
                        size="small"
                        color={estado === "Confirmado" ? "success" : "warning"}
                        variant={estado === "Confirmado" ? "filled" : "outlined"}
                    />
                );
            }
        },
        { field: "name", headerName: "Nombre", width: 120, sortable: true },
        { field: "lastName", headerName: "Apellido", width: 120, sortable: true },
        { field: "email", headerName: "Correo", width: 150, sortable: true },
        {
            field: "phone",
            headerName: "Télefono",
            width: 150,
            sortable: true,
            valueGetter: (_value, row) => {
                const { countryCode, phone } = row;
                return `${countryCode} ${phone}`;
            },
            renderCell: (params) => {
                const { countryCode, phone } = params.row;
                return `${countryCode} ${phone}`
            }
        },
        { 
            field: "aereo", 
            headerName: "# Aereos", 
            width: 120, 
            sortable: true,
            valueGetter: (_value, row) => row.aereo ?? -1,
            sortComparator: (v1, v2) => {
                if (v1 === -1 && v2 === -1) return 0;
                if (v1 === -1) return 1;
                if (v2 === -1) return -1;
                return v1 - v2;
            }
        },
        { 
            field: "maritimo", 
            headerName: "# Maritimos", 
            width: 120, 
            sortable: true,
            valueGetter: (_value, row) => row.maritimo ?? -1,
            sortComparator: (v1, v2) => {
                if (v1 === -1 && v2 === -1) return 0;
                if (v1 === -1) return 1;
                if (v2 === -1) return -1;
                return v1 - v2;
            }
        },
        { 
            field: "total", 
            headerName: "$ Ventas", 
            width: 120, 
            sortable: true,
            valueGetter: (_value, row) => row.total ?? -1,
            sortComparator: (v1, v2) => {
                if (v1 === -1 && v2 === -1) return 0;
                if (v1 === -1) return 1;
                if (v2 === -1) return -1;
                return v1 - v2;
            }
        },
        {
            field: "puntos",
            headerName: "Puntos",
            width: 100,
            sortable: true,
            valueGetter: (_value, row) => row.puntos ?? -1,
            sortComparator: (v1, v2) => {
                if (v1 === -1 && v2 === -1) return 0;
                if (v1 === -1) return 1;
                if (v2 === -1) return -1;
                return v1 - v2;
            },
            renderCell: (params) => {
                const puntos = params.row.puntos;
                return puntos !== undefined && puntos !== null ? puntos : "-";
            }
        },
        {
            field: "tarifaAerea",
            headerName: "Tarifa Aérea",
            width: 150,
            sortable: true,
            valueGetter: (_value, row) => {
                const tarifa = row.tarifaAerea;
                if (!tarifa || !tarifa.nombre) return "";
                return tarifa.nombre.toLowerCase();
            },
            renderCell: (params) => {
                const tarifa = params.row.tarifaAerea;
                if (!tarifa || !tarifa.nombre) return "-";
                return `${tarifa.nombre} - ${tarifa.moneda} ${tarifa.monto ?? 0}`;
            }
        },
        {
            field: "tarifaMaritima",
            headerName: "Tarifa Marítima",
            width: 150,
            sortable: true,
            valueGetter: (_value, row) => {
                const tarifa = row.tarifaMaritima;
                if (!tarifa || !tarifa.nombre) return "";
                return tarifa.nombre.toLowerCase();
            },
            renderCell: (params) => {
                const tarifa = params.row.tarifaMaritima;
                if (!tarifa || !tarifa.nombre) return "-";
                return `${tarifa.nombre} - ${tarifa.moneda} ${tarifa.monto ?? 0}`;
            }
        },
        {
            field: "vendedor",
            headerName: "Vendedor",
            width: 150,
            sortable: true,
            valueGetter: (_value, row) => row.vendedor?.name || "",
            renderCell: (params) => params.row.vendedor?.name || "-",
        },
        {
            field: "referidoPor",
            headerName: "Referido por",
            width: 150,
            sortable: true,
            valueGetter: (_value, row) => row.referidoPor?.name || "",
            renderCell: (params) => params.row.referidoPor?.name || "-",
        },
    ];

    return (
        <>
            {entity && (
                <>
                    <ModalFormUser
                        isOpen={isOpen}
                        onClose={handleCloseModal}
                        entity={entity}
                    />
                    <ModalDeleteUser
                        isOpen={isOpenDelete}
                        onClose={handleCloseDeleteModal}
                        usuario={entity}
                    />
                </>
            )}
            <ModalCreateUser
                isOpen={isOpenCreate}
                onClose={handleCloseCreateModal}
            />
            <ModalUserLimit
                isOpen={isOpenUserLimit}
                onClose={() => setIsOpenUserLimit(false)}
            />
            <Paper sx={{ padding: 2, width: "auto", marginTop: 2 }}>
                <Box
                    sx={{
                        display: "flex",
                        gap: 2,
                        jsutifyContent: "space-between",
                        marginBottom: 2,
                    }}
                >
                    <TextField
                        label="Buscar usuarios"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={{ width: "90%" }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<PersonAdd />}
                        onClick={handleOpenCreateModal}
                        size="small"
                    >
                        Nuevo
                    </Button>
                    <Button
                        size="small"
                        disabled={!filteredRows.slice(
                            paginationModel.page * paginationModel.pageSize,
                            (paginationModel.page + 1) * paginationModel.pageSize
                        ).length}
                        onClick={() => {
                            const fecha = dayjs().format("DD/MM/YYYY")
                            const fileName = `usuarios-${fecha}.pdf`
                            exportPDFUsers(
                                fileName,
                                filteredRows,
                                agencia?.nombre ?? "",
                            )
                        }}
                    >
                        <Tooltip title="Exportar como archivo PDF">
                            <PictureAsPdf sx={{ width: '1.2em', height: '1.2em' }} />
                        </Tooltip>
                    </Button>
                    <Button
                        size="small"
                        disabled={!filteredRows.slice(
                            paginationModel.page * paginationModel.pageSize,
                            (paginationModel.page + 1) * paginationModel.pageSize
                        ).length}
                        onClick={() => {
                            const fecha = dayjs().format("DD/MM/YYYY")
                            const fileName = `usuarios-${fecha}.xlsx`
                            exportExcelUsers(
                                fileName,
                                filteredRows,
                                agencia?.nombre ?? "",
                            )
                        }}
                    >
                        <Tooltip title="Exportar como archivo .xlsx">
                            <TableView sx={{ width: '1.2em', height: '1.2em' }} />
                        </Tooltip>
                    </Button>
                </Box>

                <DataTable<Usuario>
                    rows={filteredRows.slice(
                        paginationModel.page * paginationModel.pageSize,
                        (paginationModel.page + 1) * paginationModel.pageSize
                    )}
                    columns={columns}
                    paginationModel={paginationModel}
                    onSelectionModelChange={setSelectionModel}
                    checkboxSelection={false}
                    slots={{
                        footer: () => <GridPagination />,
                    }}
                    onRowDoubleClick={(params) => {
                        handleEdit(params.row);
                    }}
                    onPaginationModelChange={async (newModel: {
                        page: number;
                        pageSize: number;
                    }) => {
                        setPaginationModel(newModel);

                        const totalLoaded = allUsuarios.length;
                        const totalNeeded = (newModel.page + 1) * newModel.pageSize;

                        if (totalNeeded > totalLoaded) {
                            await fetchNextPage();
                        }
                    }}
                    rowCount={search ? filteredRows.length : countTotal}
                    sortingMode="client"
                />
            </Paper>
        </>
    );
}
